import apiService from './api.service';

export interface RUMSession {
  id: string;
  session_id: string;
  tenant_id: string;
  application_id: string;
  user_id?: string;
  platform: string;
  device_type?: string;
  browser_name?: string;
  browser_version?: string;
  os_name?: string;
  os_version?: string;
  country?: string;
  started_at: string;
  ended_at?: string;
  duration_ms?: number;
  page_view_count: number;
  event_count: number;
  error_count: number;
  has_recording: boolean;
  viewed?: boolean;
}

export interface RUMPageView {
  id: string;
  session_id: string;
  url: string;
  path: string;
  title?: string;
  timestamp: string;
  duration_ms?: number;
  ttfb_ms?: number;
  fcp_ms?: number;
  lcp_ms?: number;
  fid_ms?: number;
  cls?: number;
}

export interface WebVitalsStats {
  fcp: number; // avg First Contentful Paint
  lcp: number; // avg Largest Contentful Paint
  fid: number; // avg First Input Delay
  cls: number; // avg Cumulative Layout Shift
  ttfb: number; // avg Time to First Byte
}

export interface RUMOverviewStats {
  totalSessions: number;
  pageViews: number;
  errors: number;
  avgLoadTime: number;
  webVitals: WebVitalsStats;
}

export interface SessionFilters {
  applicationId: string;
  startTime?: string;
  endTime?: string;
  platform?: string;
  hasErrors?: boolean;
  hasRecording?: boolean;
  limit?: number;
  offset?: number;
}

class RUMService {
  private baseUrl = '/v1/rum';

  /**
   * Get RUM overview statistics
   */
  async getOverviewStats(
    applicationId: string,
    startTime?: string,
    endTime?: string
  ): Promise<RUMOverviewStats> {
    const params = new URLSearchParams({ application_id: applicationId });
    if (startTime) params.append('start_time', startTime);
    if (endTime) params.append('end_time', endTime);

    const response = await apiService.get(`${this.baseUrl}/overview?${params.toString()}`);
    return response.data || response;
  }

  /**
   * Get RUM sessions with filters
   */
  async getSessions(filters: SessionFilters): Promise<{
    sessions: RUMSession[];
    total: number;
  }> {
    const params = new URLSearchParams({ application_id: filters.applicationId });
    if (filters.startTime) params.append('start_time', filters.startTime);
    if (filters.endTime) params.append('end_time', filters.endTime);
    if (filters.platform) params.append('platform', filters.platform);
    if (filters.hasErrors !== undefined) params.append('has_errors', filters.hasErrors.toString());
    if (filters.hasRecording !== undefined) params.append('has_recording', filters.hasRecording.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.offset) params.append('offset', filters.offset.toString());

    const response = await apiService.get(`${this.baseUrl}/sessions?${params.toString()}`);
    return response;
  }

  /**
   * Get a single RUM session by ID
   */
  async getSession(sessionId: string): Promise<RUMSession> {
    const response = await apiService.get(`${this.baseUrl}/sessions/${sessionId}`);
    return response.data || response;
  }

  /**
   * Get page views for a session
   */
  async getPageViews(sessionId: string): Promise<RUMPageView[]> {
    const response = await apiService.get(`${this.baseUrl}/sessions/${sessionId}/page-views`);
    return response.data || response;
  }

  /**
   * Get Web Vitals statistics
   */
  async getWebVitals(
    applicationId: string,
    startTime?: string,
    endTime?: string
  ): Promise<WebVitalsStats> {
    const params = new URLSearchParams({ application_id: applicationId });
    if (startTime) params.append('start_time', startTime);
    if (endTime) params.append('end_time', endTime);

    const response = await apiService.get(`${this.baseUrl}/web-vitals?${params.toString()}`);
    return response.data || response;
  }

  /**
   * Get network requests
   */
  async getNetworkRequests(
    applicationId: string,
    startTime?: string,
    endTime?: string,
    filters?: {
      method?: string;
      statusCode?: string;
      limit?: number;
      offset?: number;
    }
  ): Promise<{
    requests: any[];
    total: number;
    stats: {
      totalRequests: number;
      avgDuration: number;
      errorRate: number;
      totalDataTransferred: number;
    };
  }> {
    const params = new URLSearchParams({ application_id: applicationId });
    if (startTime) params.append('start_time', startTime);
    if (endTime) params.append('end_time', endTime);
    if (filters?.method) params.append('method', filters.method);
    if (filters?.statusCode) params.append('status_code', filters.statusCode);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.offset) params.append('offset', filters.offset.toString());

    const response = await apiService.get(`${this.baseUrl}/network-requests?${params.toString()}`);
    return response;
  }

  async toggleSessionViewed(sessionId: string, viewed: boolean): Promise<void> {
    await apiService.patch(`${this.baseUrl}/sessions/${sessionId}/viewed`, { viewed });
  }

  /**
   * Get recording events for a session (rrweb events)
   */
  async getRecordingEvents(sessionId: string): Promise<any[]> {
    console.log('[RUM Service] Fetching recording events for session:', sessionId);
    const data = await apiService.get<{ success: boolean; data: { events: any[]; eventCount: number } }>(`${this.baseUrl}/recordings/${sessionId}`);
    console.log('[RUM Service] Raw response:', data);
    console.log('[RUM Service] data.data:', data.data);
    console.log('[RUM Service] data.data?.events:', data.data?.events);
    console.log('[RUM Service] Events length:', data.data?.events?.length || 0);
    const events = data.data?.events || [];
    console.log('[RUM Service] Returning events:', events.length);
    return events;
  }

  /**
   * Delete recording for a session
   */
  async deleteRecording(sessionId: string): Promise<void> {
    await apiService.delete(`${this.baseUrl}/recordings/${sessionId}`);
  }

  /**
   * Batch delete recordings
   */
  async batchDeleteRecordings(sessionIds: string[]): Promise<void> {
    await apiService.post(`${this.baseUrl}/recordings/batch-delete`, { sessionIds });
  }

  /**
   * Batch toggle viewed status
   */
  async batchToggleViewed(sessionIds: string[], viewed: boolean): Promise<void> {
    await apiService.post(`${this.baseUrl}/sessions/batch-viewed`, { sessionIds, viewed });
  }
}

const rumService = new RUMService();
export default rumService;
